import isEqual from "lodash/isEqual";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { doesHumanInTheLoopActionMatchToolCall } from "../human-in-the-loop";
import {
  useHumanInTheLoop,
  usePendingTranscriptToolCalls,
} from "../HumanInTheLoopContext";
import { HumanInTheLoopDecisionControls } from "../HumanInTheLoopDecisionControls";
import type { ChatToolMessage } from "../types";
import { OrdinaryToolRow } from "./OrdinaryToolRow";
import DraftSkillTool from "./tools/DraftSkillTool";
import DraftTrainingProgramTool from "./tools/DraftTrainingProgramTool";
import DraftTrainingQuizTool from "./tools/DraftTrainingQuizTool";
import EditDataKeyTool from "./tools/EditDataKeyTool";
import EditDocumentTool from "./tools/EditDocumentTool";
import { EditQualityEventRecordTool } from "./tools/EditQualityEventRecordTool";
import { GenericToolCall } from "./tools/GenericToolCall";
import { SubagentTaskTool } from "./tools/SubagentTaskTool";
import TiptapEditDocumentTool from "./tools/TiptapEditDocumentTool";
import { ToolCallName } from "./utils/tool-calls";

export type ParsedContent =
  | undefined
  | string
  | any[]
  | Record<string, unknown>;

export interface ToolCallBlockProps {
  toolCall: {
    type?: "tool_call" | "tool_call_chunk" | "invalid_tool_call";
    id?: string;
    name?: string;
    args?: unknown;
    error?: string;
  };
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
  humanInTheLoopActionIndex?: number;
  compact?: boolean;
  decision?: "approve" | "reject" | "edit";
}

/**
 * Router component that delegates to the appropriate tool component based on tool name.
 * Each tool component is self-contained and handles its own:
 * - Data fetching
 * - Title computation
 * - Actions and apply logic
 * - Frame rendering
 */
export const ToolCallBlock = observer(
  ({
    toolCall,
    toolResult,
    isLoading = false,
    humanInTheLoopActionIndex,
    compact = false,
    decision,
  }: ToolCallBlockProps) => {
    const toolName = toolCall.name ?? "";
    const humanInTheLoop = useHumanInTheLoop();
    const pendingTranscriptToolCalls = usePendingTranscriptToolCalls();
    const parseArgsString = (
      rawArgs: string
    ): Record<string, unknown> | null => {
      try {
        const parsed = JSON.parse(rawArgs) as unknown;
        if (parsed && typeof parsed === "object") {
          return parsed as Record<string, unknown>;
        }
        return null;
      } catch {
        return null;
      }
    };

    // Parse args safely based on block type
    const { args, hasArgsParseError } = useMemo((): {
      args: Record<string, unknown>;
      hasArgsParseError: boolean;
    } => {
      const rawArgs = toolCall.args;
      let args: Record<string, unknown> = {};
      let hasArgsParseError = false;

      if (typeof rawArgs === "string") {
        const parsed = parseArgsString(rawArgs);
        if (parsed) {
          args = parsed;
        } else {
          hasArgsParseError = true;
        }
      } else if (rawArgs && typeof rawArgs === "object") {
        args = rawArgs as Record<string, unknown>;
      }

      if (typeof args.input === "string") {
        const parsedInput = parseArgsString(args.input);
        if (parsedInput) {
          args = {
            ...args,
            ...parsedInput,
          };
        } else {
          hasArgsParseError = true;
        }
      }

      return { args, hasArgsParseError };
    }, [toolCall.args, toolCall.type]);

    if (
      compact &&
      toolName &&
      ![
        ToolCallName.EDIT_DATA_KEY,
        ToolCallName.LEGACY_WRITE_DATA_KEY,
        ToolCallName.TASK,
        ToolCallName.DRAFT_SKILL,
        ToolCallName.DRAFT_TRAINING_PROGRAM,
        ToolCallName.DRAFT_TRAINING_QUIZ,
      ].includes(toolName as ToolCallName)
    ) {
      return (
        <OrdinaryToolRow
          call={{ id: toolCall.id ?? toolName, toolCall, messageIndex: 0 }}
          result={toolResult}
          running={isLoading && !toolResult}
          decision={decision}
        />
      );
    }

    if (
      !toolResult &&
      pendingTranscriptToolCalls.some(
        (action) =>
          doesHumanInTheLoopActionMatchToolCall(action.name, toolName) &&
          isEqual(action.args, args)
      )
    ) {
      return null;
    }

    // Early return for missing tool name (except invalid tool calls which show errors)
    if (!toolName && toolCall.type !== "invalid_tool_call") {
      return null;
    }

    const humanInTheLoopRequest = humanInTheLoop?.request;
    const actionIndex =
      humanInTheLoopActionIndex ??
      (humanInTheLoopRequest?.action_requests.length === 1 ? 0 : undefined);
    const pendingAction =
      actionIndex !== undefined
        ? humanInTheLoopRequest?.action_requests[actionIndex]
        : undefined;
    const canReview =
      !toolResult &&
      humanInTheLoopRequest &&
      actionIndex !== undefined &&
      pendingAction &&
      doesHumanInTheLoopActionMatchToolCall(pendingAction.name, toolName);
    const renderApprovalControls = (options?: {
      approveDisabled?: boolean;
      beforeApprove?: () => Promise<boolean>;
    }) =>
      canReview ? (
        <HumanInTheLoopDecisionControls
          request={humanInTheLoopRequest}
          isLoading={humanInTheLoop?.isLoading ?? false}
          actionIndex={actionIndex}
          approveLabel={
            toolName === ToolCallName.ADD_USER_TO_FUNCTIONAL_GROUP
              ? "Join functional group"
              : undefined
          }
          rejectLabel={
            toolName === ToolCallName.ADD_USER_TO_FUNCTIONAL_GROUP
              ? "Not now"
              : undefined
          }
          approveColor={
            toolName === ToolCallName.ADD_USER_TO_FUNCTIONAL_GROUP
              ? "primary"
              : undefined
          }
          approveDisabled={options?.approveDisabled}
          beforeApprove={options?.beforeApprove}
        />
      ) : undefined;
    const approvalControls = renderApprovalControls();

    // These tools normally show their result after execution. Before approval,
    // show the requested arguments and the shared decision controls.
    if (
      canReview &&
      (toolName === ToolCallName.DRAFT_SKILL || toolName === ToolCallName.TASK)
    ) {
      return (
        <GenericToolCall
          toolName={toolName}
          args={args}
          toolResult={toolResult}
          isLoading={isLoading}
          approvalControls={approvalControls}
        />
      );
    }

    // Switch to the appropriate tool component
    switch (toolName) {
      case ToolCallName.EDIT_DATA_KEY:
      case ToolCallName.LEGACY_WRITE_DATA_KEY:
        // Keep full tool calls on specialized rendering, even when args are incomplete.
        // Streamed chunks with parse errors (or explicit invalid calls) use generic fallback.
        if (
          toolCall.type !== "tool_call" &&
          (toolCall.type === "invalid_tool_call" || hasArgsParseError)
        ) {
          return (
            <GenericToolCall
              toolName={toolName}
              args={args}
              toolResult={toolResult}
              isLoading={isLoading}
              approvalControls={approvalControls}
            />
          );
        }

        return (
          <EditDataKeyTool
            decision={decision}
            args={args}
            toolResult={toolResult}
            isLoading={isLoading}
            approvalControls={
              approvalControls ? renderApprovalControls : undefined
            }
          />
        );

      case ToolCallName.EDIT_CUSTOM_DOCUMENT_TEXT:
        return (
          <EditDocumentTool
            args={args}
            toolResult={toolResult}
            isLoading={isLoading}
            approvalControls={approvalControls}
          />
        );

      case ToolCallName.EDIT_DOCUMENT:
        return (
          <TiptapEditDocumentTool
            args={args}
            toolResult={toolResult}
            isLoading={isLoading}
            approvalControls={
              approvalControls ? renderApprovalControls : undefined
            }
          />
        );

      case ToolCallName.EDIT_QUALITY_EVENT_RECORD:
        return (
          <EditQualityEventRecordTool
            decision={decision}
            args={args}
            isLoading={isLoading}
            actionIndex={actionIndex}
          />
        );

      case ToolCallName.DRAFT_SKILL:
        return (
          <DraftSkillTool
            args={args}
            toolResult={toolResult}
            isLoading={isLoading}
          />
        );

      case ToolCallName.DRAFT_TRAINING_QUIZ:
        return (
          <DraftTrainingQuizTool
            args={args}
            toolResult={toolResult}
            isLoading={isLoading}
            actionIndex={actionIndex}
          />
        );

      case ToolCallName.DRAFT_TRAINING_PROGRAM:
        return (
          <DraftTrainingProgramTool
            args={args}
            toolResult={toolResult}
            isLoading={isLoading}
            actionIndex={actionIndex}
          />
        );

      case ToolCallName.TASK:
        return (
          <SubagentTaskTool
            toolCall={toolCall}
            args={args}
            toolResult={toolResult}
            isLoading={isLoading}
          />
        );

      default:
        // All other tools use the generic handler
        return (
          <GenericToolCall
            toolName={toolName}
            args={args}
            toolResult={toolResult}
            isLoading={isLoading}
            approvalControls={approvalControls}
          />
        );
    }
  }
);
